<template>
  <Accordion class="p-6 filter-sets bg-gray-50">
    <AccordionPanel value="0">
      <AccordionHeader class="!bg-transparent">Manage Filter Sets</AccordionHeader>
      <AccordionContent>
        <div class="filter-state-alerts mini alerts"></div>

        <!-- Filter state selection -->
        <div class="form-row flex mt-2">
          <label for="filter_state_id">Load</label>
          <div class="controls">
            <select v-model="selectedStateId" id="filter_state_id" name="filter_state_id" @change="onStateChange">
              <option value=""></option>
              <option v-for="item in sortedFilterStates" :key="item.id" :value="item.id">{{ item.name }}</option>
            </select>
          </div>
        </div>

        <div class="form-row flex mt-2">
          <label for="filter_state_name">Save As</label>
          <div class="controls">
            <input id="filter_state_name" name="filter_state_name" v-model="filterStateName" type="text" placeholder="" />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-between mt-6">
          <button @click="deleteState" v-show="selectedStateId" tyep="button" class="button secondary">
            <i class="fal fa-trash-alt"></i>
          </button>

          <div class="flex space-x-2">
            <button v-if="isUserSuperuser" v-show="selectedStateId" @click="shareState" class="secondary button">
              <i class="fal fa-share"></i>
            </button>

            <button @click="toggleFavorite" v-show="selectedStateId" :class="['button', isFavorite ? 'warning' : 'secondary']">
              <i :class="['fa', isFavorite ? 'fas' : 'fal', 'fa-star']"></i>
            </button>

            <button @click="saveNewState" class="secondary button">Save As New Filter Set</button>

            <button @click="updateState" v-show="selectedStateId" class="primary button">Save</button>
          </div>
        </div>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue'
  import { formState } from "../dependencies/formDependencies.js"

  import Accordion from 'primevue/accordion';
  import AccordionPanel from 'primevue/accordionpanel';
  import AccordionHeader from 'primevue/accordionheader';
  import AccordionContent from 'primevue/accordioncontent';

  const props = defineProps({
    currentPath:      { type: String, required: true },
    isUserSuperuser:  { type: Boolean, default: false },
    initialStateId:   { type: String, default: '' }
  })

  const emit = defineEmits([
    'filter-state-loaded',
    'filter-state-saved',
    'favorite-toggled',
    'state-shared',
    'state-deleted',
    'clear-filters'
  ])

  defineExpose({
    clearSelectedState() {
      selectedStateId.value = ''
    }
  })

  // Reactive state
  const selectedStateId = ref('') // will be filled in on mount
  const filterStateName = ref('')
  const filterStates = ref({})
  const isFavorite = ref(false)
  const filtersLoaded = ref(false)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

  const sortedFilterStates = computed(() => {
    return Object.entries(filterStates.value)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  // Methods
  // This is only called at the beginning
  const loadFilterOptions = async () => {
    // console.log('AFS: load filter state options')
    try {
      const response = await fetch(`/admin/filter_states/names?url=${props.currentPath}`)
      const data = await response.json()
      filterStates.value = data
    } catch (error) {
      console.error('Error loading filter options:', error)
    }
  }

  // NOTE: the old jquery.serialize() method is almost equivalent to `new URLSearchParams(new FormData(formElement)).toString()`
  // so if the new FormData doesn't work with our back-end, we can replace it with the code above
  const loadFilterState = async (stateId) => {
    try {
      const response = await fetch(`/admin/filter_states/load?filter_state_id=${stateId}&url=${props.currentPath}`)
      const data = await response.json()
      filterStateName.value = filterStates.value[stateId].replace(' (Favorite)', '')
      isFavorite.value = data.filter_state_favorite === '1'
      // console.log('AFS: state data: ', data)
      emit('filter-state-loaded', data)
    } catch (error) {
      console.error('Error loading filter state:', error)
    }
  }

  const updateState = async () => {
    try {
      const formData = new FormData(document.getElementById('filters-form'))
      const response = await fetch('/admin/filter_states/update_state', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken
        },
      })
      if (response.ok) {
        await loadFilterOptions()
        emit('filter-state-saved')
      }
    } catch (error) {
      console.error('Error updating state:', error)
    }
  }

  const saveNewState = async () => {
    try {
      const formData = new FormData(document.getElementById('filters-form'))
      const response = await fetch('/admin/filter_states', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken
        },
      })

      if (response.ok) {
        await loadFilterOptions()
        emit('filter-state-saved')
      }
    } catch (error) {
      console.error('Error saving new state:', error)
    }
  }

  const toggleFavorite = async () => {
    try {
      const response = await fetch('/admin/filter_states/favorite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          filter_state_id: selectedStateId.value
        })
      })

      if (response.ok) {
        const data = await response.json()
        isFavorite.value = !isFavorite.value
        await loadFilterOptions()
        emit('favorite-toggled', data)
      }
    } catch (error) {
      console.error('Error toggling favorite:', error)
    }
  }

  const shareState = async () => {
    try {
      const response = await fetch('/admin/filter_states/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          filter_state_id: selectedStateId.value
        })
      })

      if (response.ok) {
        emit('state-shared')
      }
    } catch (error) {
      console.error('Error sharing state:', error)
    }
  }

  const deleteState = async () => {
    if (!confirm('This will delete this filter set. Are you sure?')) {
      return
    }

    try {
      const formData = new FormData(document.getElementById('filters-form'))
      const response = await fetch('/admin/filter_states/delete_state', {
        method: 'DELETE',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })

      if (response.ok) {
        selectedStateId.value = ''
        clearForm()
        await loadFilterOptions()
        emit('state-deleted')
      }
    } catch (error) {
      console.error('Error deleting state:', error)
    }
  }

  const clearForm = () => {
    filterStateName.value = ''
    isFavorite.value = false
    emit('clear-filters')
  }

  // Watchers
  const onStateChange = async () => {
    // console.log('ASF: loading filter state data')
    if (selectedStateId.value) {
      await loadFilterState(selectedStateId.value)
    } else {
      clearForm()
    }
  }

  function isNumber(value) {
    return !isNaN(value);
  }

  // Lifecycle hooks
  onMounted(async () => {
    await loadFilterOptions()
    // console.log('AFS: filter states loaded')
    if (props.initialStateId && isNumber(props.initialStateId)) {
      selectedStateId.value = props.initialStateId

      await onStateChange()
      // console.log('AFS: state data loaded')
    }
  })
</script>
